import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { sponsor, sponsorClearData } from "../../../store/sponsor/actions";
import { GetProjectAll } from "../../../fnx/project/projectAll";
import { CLIENT_RULE_ENTITY_NAME } from "ah_utility/fnxUtils";
import { usePropertiesGetAll } from "../../../fnx/properties/propertiesGetAll";
import { postCollection, postCollectionClearData, } from "../../../store/actions";
import { projectClearData } from "../../../store/project/actions";
import LocalStorageService from "../../../services/localStorage";
import { buildBillingRulesConfig, FALLBACK_FIELD_VALUES, getBillingCalendarPicker, getBillingMonthMaxOffset, getBillingSelectConfigs, } from "../constants/billingRules.config";
import { getCurrentMonth, getMonthsAhead } from "../../../services/getDates";
import { BILLING_CANCEL_BUTTON_LABEL, BILLING_COLLECTION_ERROR_MESSAGES, BILLING_GENERIC_ERROR_MESSAGE, BILLING_NO_PRODUCTS_SELECTED_MESSAGE, BILLING_PRODUCTS_FETCH_LIMIT, BILLING_SUBMIT_BUTTON_LABEL, BILLING_TITLE_GENERATE, BILLING_TYPE_OPTIONS, } from "../constants/billing.constants";
var storageKey = "assurehub";
var lsLogin = new LocalStorageService(storageKey);
var currentMonthEndDate = getCurrentMonth().endDate;
var DEFAULT_FORM_VALUES = {
    billingType: "",
    month: currentMonthEndDate,
    maxPeriods: FALLBACK_FIELD_VALUES.maxPeriods,
    maxInvoices: FALLBACK_FIELD_VALUES.maxInvoices,
    cancelAfterMonths: FALLBACK_FIELD_VALUES.cancelAfterMonths,
    filePerProduct: false,
    noCancelExpiredPolicy: false,
    noExpiredPolicyAge: false,
    selectedProductIds: [],
};
export var useBilling = function () {
    var _a, _b;
    var dispatch = useDispatch();
    var navigate = useNavigate();
    var clientId = (_b = (_a = lsLogin.getUserLoginStored()) === null || _a === void 0 ? void 0 : _a.clientId) !== null && _b !== void 0 ? _b : "";
    var enable = useState(true)[0];
    var page = useState(1)[0];
    var limit = useState(5000)[0];
    var _c = useState(""), sponsorsIds = _c[0], setSponsorsIds = _c[1];
    var _d = useState(false), loading = _d[0], setLoading = _d[1];
    var _e = useState(false), triggerProject = _e[0], setTriggerProject = _e[1];
    var _f = useState(false), storeCleared = _f[0], setStoreCleared = _f[1];
    var _g = useState(false), productsLoaded = _g[0], setProductsLoaded = _g[1];
    var _h = useState(false), selectsInitialized = _h[0], setSelectsInitialized = _h[1];
    var _j = useState([]), products = _j[0], setProducts = _j[1];
    var _k = useState({
        show: false,
        message: "",
        type: "success",
    }), toast = _k[0], setToast = _k[1];
    var form = useForm({
        mode: "onChange",
        defaultValues: DEFAULT_FORM_VALUES,
    });
    var responseSponsor = useSelector(function (reduxState) { var _a; return (_a = reduxState.Sponsor.responseSponsor) === null || _a === void 0 ? void 0 : _a.data; });
    var _l = useSelector(function (reduxState) { return ({
        responseCollection: reduxState.Collections.responsePostCollection,
        errorCollection: reduxState.Collections.errorPostCollection,
    }); }), responseCollection = _l.responseCollection, errorCollection = _l.errorCollection;
    var responseProject = GetProjectAll(triggerProject, enable, 1, BILLING_PRODUCTS_FETCH_LIMIT, sponsorsIds).responseProject;
    var _m = usePropertiesGetAll(CLIENT_RULE_ENTITY_NAME), clientRuleProperties = _m.responseProperties, loadingClientRules = _m.loadingProperties;
    var rulesConfig = useMemo(function () { return buildBillingRulesConfig(clientRuleProperties, clientId); }, [clientRuleProperties, clientId]);
    var visibleBillingTypes = useMemo(function () {
        return BILLING_TYPE_OPTIONS.filter(function (option) { return !rulesConfig.hiddenBillingTypes.includes(option.value); });
    }, [rulesConfig]);
    var calendarPicker = useMemo(function () { return getBillingCalendarPicker(clientRuleProperties, clientId); }, [clientRuleProperties, clientId]);
    var monthMaxOffset = useMemo(function () { return getBillingMonthMaxOffset(clientRuleProperties, clientId); }, [clientRuleProperties, clientId]);
    var monthMaxDate = useMemo(function () { return getMonthsAhead(monthMaxOffset).endDate; }, [monthMaxOffset]);
    var selectConfigs = useMemo(function () { return getBillingSelectConfigs(clientRuleProperties, clientId); }, [clientRuleProperties, clientId]);
    var applySelectDefaults = function () {
        form.setValue("maxPeriods", selectConfigs.maxPeriods.defaultValue);
        form.setValue("cancelAfterMonths", selectConfigs.cancelAfterMonths.defaultValue);
        form.setValue("maxInvoices", selectConfigs.maxInvoices.defaultValue);
    };
    var dataLoaded = productsLoaded && !loadingClientRules;
    useEffect(function () {
        var _a, _b;
        if (loadingClientRules)
            return;
        var currentBillingType = form.getValues("billingType");
        var isCurrentVisible = visibleBillingTypes.some(function (option) { return option.value === currentBillingType; });
        if (!isCurrentVisible) {
            form.setValue("billingType", (_b = (_a = visibleBillingTypes[0]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : "");
        }
    }, [loadingClientRules, visibleBillingTypes]);
    useEffect(function () {
        if (!dataLoaded || selectsInitialized)
            return;
        applySelectDefaults();
        setSelectsInitialized(true);
    }, [dataLoaded, selectsInitialized, selectConfigs]);
    useEffect(function () {
        dispatch(sponsorClearData());
        dispatch(projectClearData());
        setStoreCleared(true);
        return function () {
            dispatch(sponsorClearData());
            dispatch(projectClearData());
        };
    }, []);
    useEffect(function () {
        if (!storeCleared)
            return;
        dispatch(sponsor({
            enable: enable,
            page: page,
            limit: limit,
            clientId: clientId,
        }));
    }, [storeCleared]);
    useEffect(function () {
        if (!storeCleared)
            return;
        if (responseSponsor && responseSponsor.length > 0) {
            var idSponsor = responseSponsor
                .map(function (item) { return item.id; })
                .join("&sponsorId=");
            setSponsorsIds(idSponsor);
            setTriggerProject(true);
        }
    }, [responseSponsor, storeCleared]);
    useEffect(function () {
        if (!responseProject)
            return;
        var newProducts = responseProject.data
            .filter(function (item) { return item.visibleInBilling === true; })
            .map(function (item) {
            var _a, _b, _c, _d, _e;
            return ({
                id: item.id,
                description: (_a = item.description) !== null && _a !== void 0 ? _a : "",
                sponsorName: (_c = (_b = item.fkSponsor) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : "",
                code: (_d = item.code) !== null && _d !== void 0 ? _d : "",
                isLegacy: ((_e = item.metadata) === null || _e === void 0 ? void 0 : _e.isLegacy) === true,
            });
        });
        setProducts(newProducts);
        form.setValue("selectedProductIds", newProducts.map(function (product) { return product.id; }));
        setProductsLoaded(true);
        dispatch(projectClearData());
    }, [responseProject]);
    useEffect(function () {
        if (!responseCollection)
            return;
        var timeoutId = setTimeout(function () {
            dispatch(postCollectionClearData());
            setLoading(false);
            navigate("/admin/cobranza");
        }, 2000);
        return function () { return clearTimeout(timeoutId); };
    }, [responseCollection]);
    useEffect(function () {
        var _a, _b, _c;
        if (!errorCollection)
            return;
        setLoading(false);
        var responseError = errorCollection;
        var errorCode = (_c = (_b = (_a = responseError === null || responseError === void 0 ? void 0 : responseError.response) === null || _a === void 0 ? void 0 : _a.errors) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.message;
        var message = (errorCode && BILLING_COLLECTION_ERROR_MESSAGES[errorCode]) ||
            BILLING_GENERIC_ERROR_MESSAGE;
        setToast({
            show: true,
            type: "error",
            message: message,
        });
    }, [errorCollection]);
    var onClickCancel = function () {
        navigate("/admin/cobranza");
    };
    var onSubmit = form.handleSubmit(function (values) {
        var _a, _b;
        if (values.selectedProductIds.length === 0) {
            setToast({
                show: true,
                type: "error",
                message: BILLING_NO_PRODUCTS_SELECTED_MESSAGE,
            });
            return;
        }
        var dataCollection = {
            actualMonth: values.month === "" ? currentMonthEndDate : values.month,
            cancelMonth: rulesConfig.hiddenFields.includes("cancelAfterMonths")
                ? 0
                : parseInt(values.cancelAfterMonths),
            maxInvoicesPerPolicy: rulesConfig.hiddenFields.includes("maxInvoices")
                ? 0
                : parseInt(values.maxInvoices),
            projectsId: values.selectedProductIds,
            filePerProduct: values.filePerProduct,
            userId: (_b = (_a = lsLogin.getUserLoginStored()) === null || _a === void 0 ? void 0 : _a.userId) !== null && _b !== void 0 ? _b : "",
            billingType: values.billingType,
            noCancelPolicy: values.noCancelExpiredPolicy,
            maxRebillingMonths: rulesConfig.hiddenFields.includes("maxPeriods")
                ? 0
                : parseInt(values.maxPeriods),
        };
        setLoading(true);
        dispatch(postCollection(dataCollection));
    });
    var onClickDefaultValues = function () {
        applySelectDefaults();
        form.setValue("filePerProduct", false);
        form.setValue("noCancelExpiredPolicy", false);
        form.setValue("noExpiredPolicyAge", false);
        form.setValue("month", currentMonthEndDate);
    };
    var closeToast = function () {
        setToast({ show: false, message: "", type: "success" });
    };
    var bottomBarButtons = [
        {
            id: "cancel",
            label: BILLING_CANCEL_BUTTON_LABEL,
            variant: "default",
            onClick: onClickCancel,
        },
        {
            id: "submit",
            label: BILLING_SUBMIT_BUTTON_LABEL,
            color: "brand",
            type: "submit",
        },
    ];
    return {
        dataLoaded: dataLoaded,
        loading: loading,
        toast: toast,
        closeToast: closeToast,
        title: BILLING_TITLE_GENERATE,
        control: form.control,
        products: products,
        visibleBillingTypes: visibleBillingTypes,
        calendarPicker: calendarPicker,
        monthMaxDate: monthMaxDate,
        selectConfigs: selectConfigs,
        isOptionVisible: function (option) { return !rulesConfig.hiddenOptions.includes(option); },
        isFieldVisible: function (field) { return !rulesConfig.hiddenFields.includes(field); },
        isOptionDisabled: function (option) { return rulesConfig.disabledOptions.includes(option); },
        isFieldDisabled: function (field) { return rulesConfig.disabledFields.includes(field); },
        bottomBarButtons: bottomBarButtons,
        onSubmit: onSubmit,
        onClickCancel: onClickCancel,
        onClickDefaultValues: onClickDefaultValues,
    };
};
